import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { TrendingUp, Wallet, Briefcase, BadgeCheck, AlertCircle, ArrowRight, MessageCircle } from "lucide-react";

export default function CustomerDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [lots, setLots] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/customer/dashboard/stats").then((r) => setStats(r.data));
    api.get("/customer/my-lots").then((r) => setLots(r.data));
  }, []);

  const kycPending = user?.kyc_status !== "approved";

  return (
    <div>
      {/* Hero banner */}
      <div className="relative overflow-hidden rounded-3xl mb-6 p-6 md:p-10 text-white"
        style={{ background: "linear-gradient(135deg, #1F1816 0%, #7A1010 60%, #D71921 100%)" }}>
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#F2C97A]/25 blur-3xl" />
        <div className="absolute -bottom-32 -left-16 w-72 h-72 rounded-full bg-[#3F6B3F]/20 blur-3xl" />
        <div className="relative">
          <div className="text-xs uppercase tracking-widest text-white/70 font-bold">Hello {user?.full_name?.split(" ")[0] || "investor"} 👋</div>
          <h1 className="font-display text-3xl md:text-5xl tracking-tight">Your investment dashboard</h1>
          <p className="text-white/80 mt-2">Track every animal, every rupee, every gain.</p>
        </div>
      </div>

      {kycPending && (
        <div className="gf-card p-5 mb-6 flex items-center gap-4 bg-amber-50 border-amber-200" data-testid="kyc-banner">
          <AlertCircle className="w-6 h-6 text-amber-600" />
          <div className="flex-1">
            <div className="font-medium">Complete your KYC to start investing</div>
            <p className="text-sm text-[#6B5F58]">It only takes a minute. Approval within 24 hours.</p>
          </div>
          <button onClick={() => navigate("/kyc")} data-testid="kyc-banner-cta"
            className="px-4 py-2 rounded-xl bg-[#D71921] text-white text-sm hover:bg-[#B0151E]">
            Complete KYC
          </button>
        </div>
      )}

      {/* Stats grid - colorful gradient cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { icon: Wallet, label: "Invested", value: `₹${(stats?.total_invested || 0).toLocaleString()}`, gradient: "linear-gradient(135deg, #D71921 0%, #B0151E 100%)", text: "white", testid: "stat-invested" },
          { icon: Briefcase, label: "Active Lots", value: stats?.active_lots ?? 0, gradient: "linear-gradient(135deg, #3F6B3F 0%, #2A4A2A 100%)", text: "white", testid: "stat-active" },
          { icon: BadgeCheck, label: "Sold Lots", value: stats?.sold_lots ?? 0, gradient: "linear-gradient(135deg, #B8862A 0%, #8E6517 100%)", text: "white", testid: "stat-sold" },
          { icon: TrendingUp, label: "Profit", value: `₹${Math.round(stats?.total_profit || 0).toLocaleString()}`, gradient: "linear-gradient(135deg, #1F1816 0%, #3A2925 100%)", text: "white", testid: "stat-profit" },
        ].map((s, i) => (
          <div key={s.label} className={`rounded-2xl p-5 animate-in delay-${i+1} relative overflow-hidden`}
            style={{ background: s.gradient, color: s.text }} data-testid={s.testid}>
            <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-white/10 blur-xl" />
            <s.icon className="w-5 h-5 relative" />
            <div className="text-xs uppercase tracking-wider opacity-80 mt-3 relative">{s.label}</div>
            <div className="font-display text-2xl mt-1 relative">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="flex items-end justify-between mb-4">
            <h2 className="font-display text-2xl">Your portfolio</h2>
            <Link to="/portfolio" className="text-sm text-[#D71921] underline">View all</Link>
          </div>
          {lots.length === 0 ? (
            <div className="gf-card p-10 text-center">
              <img src="https://images.unsplash.com/photo-1692364541402-4aada85a6c4a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxhZ3JpY3VsdHVyZSUyMGludmVzdG1lbnQlMjBwbGFudHxlbnwwfHx8fDE3NzcxNzg3ODR8MA&ixlib=rb-4.1.0&q=85"
                alt="" className="w-32 h-32 mx-auto rounded-2xl object-cover mb-4 opacity-90" />
              <div className="font-display text-xl">No lots yet</div>
              <p className="text-sm text-[#6B5F58] mt-1">Browse available lots and start your first investment.</p>
              <button onClick={() => navigate("/lots")} disabled={kycPending}
                className="mt-4 px-5 py-2.5 rounded-xl bg-[#D71921] text-white text-sm disabled:opacity-50">
                {kycPending ? "Approve KYC first" : "Browse lots"}
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {lots.slice(0, 4).map((l) => (
                <Link key={l.id} to={`/portfolio/${l.id}`}
                  className="gf-card p-5 flex items-center justify-between hover:border-[#D71921] transition-all"
                  data-testid={`portfolio-row-${l.lot_code}`}>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#6B5F58]">{l.lot_code}</div>
                    <div className="font-display text-lg">{l.name}</div>
                    <div className="text-xs text-[#6B5F58] mt-1">{l.animal_count} animals · {l.status.replace("_", " ")}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-xl">₹{l.price_per_lot.toLocaleString()}</div>
                    <div className="text-xs text-[#3F6B3F] flex items-center justify-end gap-1 mt-1">View <ArrowRight className="w-3 h-3" /></div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="relative overflow-hidden rounded-2xl p-6 text-white"
            style={{ background: "linear-gradient(135deg, #B8862A 0%, #D71921 100%)" }}>
            <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-[#F2C97A]/30 blur-2xl" />
            <MessageCircle className="w-8 h-8 text-white relative" />
            <div className="font-display text-2xl mt-3 relative">Talk to EarnBot</div>
            <p className="text-sm text-white/85 mt-1 relative">Got questions about lots, KYC, or selling? Our AI assistant is here 24×7.</p>
            <Link to="/chat" data-testid="chatbot-cta"
              className="relative inline-block mt-4 px-4 py-2 rounded-xl bg-white text-[#D71921] text-sm font-semibold hover:bg-[#FCEDED]">
              Open Chat
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
